import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import { t } from "i18next";
import {
  allowedFileTypes,
  allowedImagesTypes,
  ATTACHMENTS_TYPE,
  DOC_TYPE_ID,
  PHOTO_NOTES,
  Visitor,
} from "../../constants/common";
import CustomTooltip from "../tooltip";
import FileUploadInput from "../inputs/FileUploadInput";
import toast from "react-hot-toast";
import ViewAttachmentModal from "../modal/ViewAttachmentModal";

interface AttachmentsGroupCardProps {
  control: any;
  errors: any;

  uploadPhotoRef: React.MutableRefObject<HTMLInputElement | null>;
  photoFileName: string;
  setPhotoFileName: React.Dispatch<React.SetStateAction<string>>;
  setPhotoFileType: Dispatch<SetStateAction<string>>;

  uploadEidRef: React.MutableRefObject<HTMLInputElement | null>;
  eidFileName: string;
  setEidFileName: React.Dispatch<React.SetStateAction<string>>;
  setEidFileType: Dispatch<SetStateAction<string>>;

  uploadPassportRef: MutableRefObject<HTMLInputElement | null>;
  passportFileName: string;
  setPassportFileName: Dispatch<SetStateAction<string>>;
  setPassportFileType: Dispatch<SetStateAction<string>>;

  uploadVisaRef: MutableRefObject<HTMLInputElement | null>;
  visaFileName: string;
  setVisaFileName: Dispatch<SetStateAction<string>>;
  setVisaFileType: Dispatch<SetStateAction<string>>;

  uploadVehicleRegLicenseRef: MutableRefObject<HTMLInputElement | null>;
  setVehicleRegLicenseFileName: Dispatch<SetStateAction<string>>;
  setVehicleRegLicenseFileType: Dispatch<SetStateAction<string>>;
  vehicleRegLicenseFileName: string;

  uploadIqamaRef?: React.MutableRefObject<HTMLInputElement | null>;
  iqamaFileName?: string;
  setIqamaFileName?: React.Dispatch<React.SetStateAction<string>>;
  setIqamaFileType?: Dispatch<SetStateAction<string>>;

  uploadIdOtherRef?: React.MutableRefObject<HTMLInputElement | null>;
  idOtherFileName?: string;
  setIdOtherFileName?: React.Dispatch<React.SetStateAction<string>>;
  setIdOtherFileType?: Dispatch<SetStateAction<string>>;

  uploadHostEmployeeCardRef: MutableRefObject<HTMLInputElement | null>;
  hostEmployeeCardFileName: string;
  setHostEmployeeCardFileName: Dispatch<SetStateAction<string>>;
  setHostEmployeeCardFileType: Dispatch<SetStateAction<string>>;

  selectedIdType?: number;
  isVip?: boolean;

  editingVisitor: Visitor | null;

  isAttachments: {
    havePhoto: number;
    haveEid: number;
    havePassport: number;
    haveVehicleRegistration: number;
    haveIqama: number;
    haveOthers: number;
    haveVisa: number;
    haveHostEmployeeCard: number;
  };
  onAttachmentRemoved?: (info: { S_FileFieldName: string; s_FiledFileType: string }) => void;
}

const AttachmentsGroupCard = ({
  control,
  errors,
  uploadPhotoRef,
  photoFileName,
  setPhotoFileName,
  setPhotoFileType,
  uploadEidRef,
  eidFileName,
  setEidFileName,
  setEidFileType,
  uploadPassportRef,
  passportFileName,
  setPassportFileName,
  setPassportFileType,

  uploadVisaRef,
  visaFileName,
  setVisaFileName,
  setVisaFileType,

  selectedIdType,
  isVip,

  uploadVehicleRegLicenseRef,
  setVehicleRegLicenseFileName,
  setVehicleRegLicenseFileType,
  vehicleRegLicenseFileName,

  uploadIqamaRef,
  iqamaFileName,
  setIqamaFileName,
  setIqamaFileType,

  uploadIdOtherRef,
  idOtherFileName,
  setIdOtherFileName,
  setIdOtherFileType,

  uploadHostEmployeeCardRef,
  hostEmployeeCardFileName,
  setHostEmployeeCardFileName,
  setHostEmployeeCardFileType,

  editingVisitor,
  isAttachments,
  onAttachmentRemoved,
}: AttachmentsGroupCardProps) => {
  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});

  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (editingVisitor) {
          let file;
          if (type === ATTACHMENTS_TYPE.PHOTO) {
            file = (editingVisitor as any)?.uploadPhoto;
          } else if (type === ATTACHMENTS_TYPE.EID) {
            file = (editingVisitor as any)?.uploadEid;
          } else if (type === ATTACHMENTS_TYPE.PASSPORT) {
            file = (editingVisitor as any)?.uploadPassport;
          } else if (type === ATTACHMENTS_TYPE.HOST_EMPLOYEE_CARD) {
            file = (editingVisitor as any)?.uploadHostEmployeeCard;
          } else {
            file = (editingVisitor as any)?.uploadVehicleRegLicense;
          }

          if (file) {
            const url = URL.createObjectURL(file);
            setImageSrc(url);
            setContentType(file.type);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [editingVisitor, previewFiles]
  );

  return (
    <>
      <div className=" flex flex-col gap-3 lg:gap-6">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
          <FileUploadInput
            name="uploadPhoto"
            label={t("uploadPhoto")}
            required={false}
            accept=".png,.jpg,.jpeg"
            Tooltip={
              <CustomTooltip>
                <p>{t("uploadPassportPhoto")}</p>
                <p>{t("ensureClearImage")}</p>
              </CustomTooltip>
            }
            allowedTypes={allowedImagesTypes}
            control={control}
            uploadRef={uploadPhotoRef}
            fileName={photoFileName}
            setFileName={setPhotoFileName}
            setFileType={setPhotoFileType}
            error={errors.uploadPhoto}
            handleAttachmentView={handleAttachmentView}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
            attachmentType={ATTACHMENTS_TYPE.PHOTO}
            isAttachments={isAttachments?.havePhoto}
            S_FileFieldName="havePhoto"
            s_FiledFileType="_"
            onRemoveAttachment={onAttachmentRemoved}
          />

          {selectedIdType === DOC_TYPE_ID.IQAMA && (
            <FileUploadInput
              name="uploadIqama"
              label={t("uploadIqama")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              control={control}
              uploadRef={uploadIqamaRef}
              fileName={iqamaFileName}
              setFileName={setIqamaFileName}
              setFileType={setIqamaFileType}
              error={errors.uploadIqama}
              required={!isVip}
              handleAttachmentView={handleAttachmentView}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              attachmentType={ATTACHMENTS_TYPE.IQAMA}
              isAttachments={isAttachments?.haveIqama}
              S_FileFieldName="haveIqama"
              s_FiledFileType="_"
              onRemoveAttachment={onAttachmentRemoved}
            />
          )}

          {(!selectedIdType || selectedIdType === DOC_TYPE_ID.NATIONAL_ID) && (
            <FileUploadInput
              name="uploadEid"
              label={t("uploadNationalID")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              control={control}
              uploadRef={uploadEidRef}
              fileName={eidFileName}
              setFileName={setEidFileName}
              setFileType={setEidFileType}
              error={errors.uploadEid}
              required={!isVip}
              handleAttachmentView={handleAttachmentView}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              attachmentType={ATTACHMENTS_TYPE.EID}
              isAttachments={isAttachments?.haveEid}
              S_FileFieldName="haveEid"
              s_FiledFileType="_"
              onRemoveAttachment={onAttachmentRemoved}
            />
          )}

          {selectedIdType === DOC_TYPE_ID.PASSPORT && (
            <FileUploadInput
              name="uploadPassport"
              label={t("uploadPassport")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              control={control}
              uploadRef={uploadPassportRef}
              fileName={passportFileName}
              setFileName={setPassportFileName}
              setFileType={setPassportFileType}
              error={errors.uploadPassport}
              required={!isVip}
              handleAttachmentView={handleAttachmentView}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              attachmentType={ATTACHMENTS_TYPE.PASSPORT}
              isAttachments={isAttachments?.havePassport}
              S_FileFieldName="havePassport"
              s_FiledFileType="_"
              onRemoveAttachment={onAttachmentRemoved}
            />
          )}

          {selectedIdType === DOC_TYPE_ID.VISA && (
            <FileUploadInput
              name="uploadVisa"
              label={t("uploadVisa")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              control={control}
              uploadRef={uploadVisaRef}
              fileName={visaFileName}
              setFileName={setVisaFileName}
              setFileType={setVisaFileType}
              error={errors.uploadVisa}
              required={!isVip}
              handleAttachmentView={handleAttachmentView}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              attachmentType={ATTACHMENTS_TYPE.VISA}
              isAttachments={isAttachments?.haveVisa}
              S_FileFieldName="haveVisa"
              s_FiledFileType="_"
              onRemoveAttachment={onAttachmentRemoved}
            />
          )}

          {selectedIdType === DOC_TYPE_ID.OTHER && (
            <FileUploadInput
              name="uploadIdOther"
              label={t("uploadDocumentOther")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              control={control}
              uploadRef={uploadIdOtherRef}
              fileName={idOtherFileName}
              setFileName={setIdOtherFileName}
              setFileType={setIdOtherFileType}
              error={errors.uploadIdOther}
              required={!isVip}
              handleAttachmentView={handleAttachmentView}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              attachmentType={ATTACHMENTS_TYPE.OTHERS}
              isAttachments={isAttachments?.haveOthers}
              S_FileFieldName="haveOthers"
              s_FiledFileType="_"
              onRemoveAttachment={onAttachmentRemoved}
            />
          )}

          <FileUploadInput
            name="uploadVehicleRegLicense"
            label={t("vehicleRegistrationLicense")}
            accept=".png,.jpg,.jpeg,.pdf"
            allowedTypes={allowedFileTypes}
            control={control}
            uploadRef={uploadVehicleRegLicenseRef}
            fileName={vehicleRegLicenseFileName}
            setFileName={setVehicleRegLicenseFileName}
            setFileType={setVehicleRegLicenseFileType}
            required={false}
            handleAttachmentView={handleAttachmentView}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
            attachmentType={ATTACHMENTS_TYPE.VEHICLE}
            isAttachments={isAttachments?.haveVehicleRegistration}
            S_FileFieldName="haveVehicleRegistration"
            s_FiledFileType="_"
            onRemoveAttachment={onAttachmentRemoved}
          />

          <FileUploadInput
            name="uploadHostEmployeeCard"
            label={t("hostEmployeeCard")}
            accept=".png,.jpg,.jpeg,.pdf"
            allowedTypes={allowedFileTypes}
            control={control}
            uploadRef={uploadHostEmployeeCardRef}
            fileName={hostEmployeeCardFileName}
            setFileName={setHostEmployeeCardFileName}
            setFileType={setHostEmployeeCardFileType}
            error={errors.uploadHostEmployeeCard}
            handleAttachmentView={handleAttachmentView}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
            attachmentType={ATTACHMENTS_TYPE.HOST_EMPLOYEE_CARD}
            isAttachments={isAttachments?.haveHostEmployeeCard}
            S_FileFieldName="haveHostEmployeeCard"
            s_FiledFileType="_"
            onRemoveAttachment={onAttachmentRemoved}
          />
        </div>
        <p className="text-red-600 text-xs mt-4">
          <span className="font-semibold">{t("note")}:</span>{" "}
          <ul className="list-disc list-inside">
            {PHOTO_NOTES.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </p>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        isOpen={isViewAttachmentModalOpen}
        contentType={contentType}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default React.memo(AttachmentsGroupCard);
